import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ExecuteIcon,
} from '@mui/icons-material';
import { workflowService } from '../services/workflowService';
import { useNotificationStore } from '../store/notificationStore';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import type { WorkflowDetailDto } from '../services/workflowService';

/**
 * Workflow detail page component.
 */
export const WorkflowDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [workflow, setWorkflow] = useState<WorkflowDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (id) {
      loadWorkflow();
    }
  }, [id]);

  const loadWorkflow = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await workflowService.getWorkflow(id);
      setWorkflow(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load workflow';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm(`Are you sure you want to delete "${workflow?.name}"?`)) return;
    try {
      await workflowService.deleteWorkflow(id);
      addNotification('Workflow deleted successfully', 'success');
      navigate('/workflows');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete workflow', 'error');
    }
  };

  const handleExecute = async () => {
    if (!id) return;
    setExecuting(true);
    try {
      const response = await workflowService.executeWorkflow(id);
      addNotification(`Workflow execution started: ${response.executionId}`, 'success');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to execute workflow', 'error');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <PageDetailLayout
      title={workflow?.name || 'Workflow'}
      subtitle={workflow?.description}
      breadcrumbs={[{ label: 'Workflows', href: '/workflows' }, { label: workflow?.name || 'Workflow' }]}
      backTo={{ label: 'Workflows', href: '/workflows' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={workflow ? { label: workflow.isActive ? 'Active' : 'Inactive', color: workflow.isActive ? 'success' : 'default' } : undefined}
      chips={workflow ? [
        { label: workflow.workflowType, variant: 'outlined' as const },
        ...(workflow.patternType !== 'None' ? [{ label: workflow.patternType, variant: 'outlined' as const }] : []),
        { label: `v${workflow.version}`, variant: 'outlined' as const },
        ...(workflow.supportsCheckpointing ? [{ label: 'Checkpointing', variant: 'outlined' as const }] : []),
        ...(workflow.supportsStreaming ? [{ label: 'Streaming', variant: 'outlined' as const }] : []),
      ] : []}
      actions={workflow ? [
        {
          icon: executing ? <CircularProgress size={16} /> : <ExecuteIcon />,
          label: 'Execute',
          onClick: handleExecute,
          variant: 'contained' as const,
          disabled: executing || !workflow.isActive,
        },
        { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/workflows/${id}/edit`) },
        { icon: <DeleteIcon />, label: 'Delete', onClick: handleDelete, color: 'error' as const },
      ] : []}
      tabs={workflow ? [
        {
          label: 'Definition',
          value: 'definition',
          content: (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }} fontWeight={600}>
                Workflow JSON Definition
              </Typography>
              <Card variant="outlined" sx={{ p: 3, backgroundColor: 'background.default' }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: 600,
                  }}
                >
                  {JSON.stringify(JSON.parse(workflow.workflowJson), null, 2)}
                </Box>
              </Card>
            </Box>
          ),
        },
        {
          label: 'Executions',
          value: 'executions',
          content: (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }} fontWeight={600}>
                Recent Executions
              </Typography>
              <Alert severity="info">
                Execution history will be displayed here. For now, use the Execute button to start a new execution.
              </Alert>
            </Box>
          ),
        },
      ] : []}
    />
  );
};
